/**
 * Which basic transport types connect to a given station, derived once from
 * the board graph. Used to show "what kind of stop is this" — info that's
 * otherwise hidden under a pawn on the map.
 */

import { SY_BOARD } from '@mdev/common'
import type { Board } from '@mdev/common'

const board = SY_BOARD as Board

export type BasicTransport = 'taxi' | 'bus' | 'underground'

// Render order: most "powerful" first, so a chip reads underground → bus → taxi.
const ORDER: BasicTransport[] = [ 'underground', 'bus', 'taxi' ]

const byStation: ReadonlyMap<number, Set<BasicTransport>> = ( () => {
  const m = new Map<number, Set<BasicTransport>>()
  for ( const s of board.stations ) m.set( s.id, new Set() )
  for ( const e of board.edges ) {
    if ( e.transport === 'ferry' ) continue
    m.get( e.from )?.add( e.transport as BasicTransport )
    m.get( e.to )?.add( e.transport as BasicTransport )
  }
  return m
} )()

/** Distinct transport types at this station, ordered underground → bus → taxi. */
export function stationTransports( id: number ): BasicTransport[] {
  const set = byStation.get( id )
  if ( !set ) return []
  return ORDER.filter( ( t ) => set.has( t ) )
}

/** Pill colours by transport tier — saturated for white-text legibility. */
export const STATION_TYPE_COLOR: Record<BasicTransport, string> = {
  underground: '#EF4444',
  bus: '#3B82F6',
  taxi: '#F59E0B',
}

/** Colour of a station by its dominant tier (underground > bus > taxi). */
export function stationColor( id: number ): string {
  const t = stationTransports( id )
  return t.length > 0 ? STATION_TYPE_COLOR[ t[ 0 ] ] : '#6B7280'
}
